// Layer 3 (Transformations): Manager "Escalated issues" and "Performance vs
// budget" tab bodies — map-role-dashboards.md §8b–8c. Recovery % rows derive
// from the seed series at the session cursor; budget columns read target,
// design is labelled design (decisions.md § recovery basis).

import type { SessionState } from '@/versions/v1/schema/scenario';
import type { FmeaLearningSessionState } from '@/versions/v1/shared/FmeaLearningEngine';
import { FAILURE_MODES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { recoveryPtDelta } from '@/versions/v1/shared/recovery-vocab.derive';
import { readingsAt } from '@/versions/v1/shared/series.derive';
import type { VizTone } from '@/versions/v1/modules/_viz';
import { fmeaCasePhase, fmeaCaseRead } from '../advisory/FmeaLearning.transform';
import {
  formatDateOffsetFromCursor,
  formatDueFromCursor,
  mtdPlanRevisionRead,
} from './LandingCursor.transform';
import { gmMoney, gmBudgetTone, gmAmount, gmThousands, gmSignedAmount } from './GmMoney.transform';
import { ANNUAL_PLAN_BUDGETS, PLANNING_MONTH_PROFILE } from '@/versions/v1/shared/bunker-hill-planning-seed';
import { panelBNightPattern } from './ShiftHeatmap.transform';
import { formatAnnualA, valueBandRead } from '../advisory/ValueBand.transform';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';

/** Who holds each seat on an escalation's decision gate (comp `.fld` label · actor chip). */
export type EscalationSeat = { role: 'Decides' | 'Confirms' | 'Owns'; holder: string; confirmer?: string };

export type EscalationCard = {
  id: string;
  /** A decision closes with the manager's record; a pattern is owned below the manager and only noted. */
  kind: 'decision' | 'pattern';
  /** Rail-header bucket: due today, capital for review, or the rest of next week. */
  group: 'today' | 'capital' | 'later';
  title: string;
  /** Plain text of the financial line (probes read it); `financialParts` bolds its figures. */
  financial: string;
  financialParts: readonly { text: string; strong?: boolean }[];
  hypotheses?: readonly string[];
  code?: string;
  /** Meta line under the financial line: who carries the work. */
  owner: string;
  /** The question the gate closes. */
  decision: string;
  /** Decision field choices; the first is the proposal. */
  options: readonly string[];
  seats: readonly EscalationSeat[];
  date: string;
  due: string;
  dueTone: 'bad' | 'warn' | 'info' | 'neutral';
};

function joinParts(parts: readonly { text: string }[]): string {
  return parts.map((part) => part.text).join('');
}

/** The gate's Date field reads a two-digit day (`08 Jun`), as the record stamp does. */
function padDay(label: string): string {
  return label.replace(/^(\d)(?=\s)/, '0$1');
}

/** The five escalations, in the comp's rail order: BM01 (due today), the
 * capital pair, the plan revision, then the Panel B pattern (quiet, last). */
export function escalationCards(state: SessionState): EscalationCard[] {
  const bearingStop = gmMoney().bearingStop;
  const amount = (value: number) => `${CURRENCY_GLYPH}${value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const bearingParts = [
    { text: `${bearingStop.plannedHours} h planned inspection ` },
    { text: amount(bearingStop.plannedAmount), strong: true },
    { text: ` vs ${bearingStop.avoidedFailureHours} h avoided failure ` },
    { text: amount(bearingStop.avoidedFailureAmount), strong: true },
  ];
  const capitalDue = formatDueFromCursor(state, { kind: 'offset', days: 11 });
  const plan = mtdPlanRevisionRead(state);
  const planDue = formatDueFromCursor(state, { kind: 'offset', days: 8 });
  const planParts = [
    { text: 'MTD run-rate forecast ' },
    { text: `${plan.forecastTonnes.toLocaleString()} dst`, strong: true },
    { text: ` vs ${plan.monthPlan.toLocaleString()} dst plan · ${plan.mtdActual.toLocaleString()} actual vs ${plan.mtdPlan.toLocaleString()} plan MTD` },
  ];
  const panelB = panelBNightPattern(state);
  const panelBDue = formatDueFromCursor(state, { kind: 'offset', days: 13 });
  const panelBParts = [
    { text: `${panelB.panelBNightInWorst} of the ${panelB.worstCount} lowest Zn recovery shifts this week · Panel B night ` },
    { text: `${panelB.panelBAvg.toFixed(1)}%`, strong: true },
    { text: ` vs ${panelB.otherAvg.toFixed(1)}% on other shifts` },
  ];
  const slakerParts = [{ text: 'Recurring lime depletion disrupts recovery; the slaker upgrade reduces manual lime handling.' }];
  const vsdParts = [{ text: 'Concentrate-pump seal failures recur; the VSD upgrade addresses pump operation.' }];
  return [
    {
      id: 'esc-1',
      kind: 'decision',
      group: 'today',
      title: 'BM01 trunnion bearing — controlled inspection stop',
      financial: joinParts(bearingParts),
      financialParts: bearingParts,
      code: 'BM-BRG',
      // WO-441 is the open bearing work order assigned to J. Smith (maintenance overview seed).
      owner: 'WO-441 · J. Smith · Maintenance',
      decision: `Stop BM01 for the ${bearingStop.plannedHours} h controlled inspection in this week’s window, once the 16:00 oil Fe result confirms`,
      options: ['Approve stop', 'Defer'],
      seats: [
        { role: 'Decides', holder: 'GM' },
        { role: 'Confirms', holder: 'B. Fletcher · Maint Supt', confirmer: 'Maintenance Supt' },
      ],
      date: padDay(formatDateOffsetFromCursor(state, 0)),
      due: formatDueFromCursor(state, { kind: 'today', hm: 'EOD' }),
      dueTone: 'bad',
    },
    {
      id: 'esc-2',
      kind: 'decision',
      group: 'capital',
      title: 'Lime slaker upgrade business case (IMP-003)',
      financial: joinParts(slakerParts),
      financialParts: slakerParts,
      owner: `Maintenance Superintendent submits by ${capitalDue}`,
      decision: 'Manager review of the capital business case',
      options: ['Approve capex', 'Defer'],
      seats: [{ role: 'Decides', holder: 'GM' }],
      date: padDay(capitalDue),
      due: capitalDue,
      dueTone: 'warn',
    },
    {
      id: 'esc-3',
      kind: 'decision',
      group: 'capital',
      title: 'VSD upgrade business case (IMP-005)',
      financial: joinParts(vsdParts),
      financialParts: vsdParts,
      owner: 'Submitted alongside IMP-003',
      decision: 'Decide with IMP-003: the combined case is stronger',
      options: ['Approve capex', 'Defer'],
      seats: [{ role: 'Decides', holder: 'GM' }],
      date: padDay(capitalDue),
      due: capitalDue,
      dueTone: 'warn',
    },
    {
      id: 'esc-5',
      kind: 'decision',
      group: 'later',
      title: 'MTD production plan revision',
      financial: joinParts(planParts),
      financialParts: planParts,
      owner: 'Ops Superintendent and Mine Planning',
      decision: `If the run-rate holds through ${planDue}: revise the MTD plan and the Finance forecast`,
      options: ['Revise plan', 'Defer'],
      seats: [{ role: 'Decides', holder: 'GM' }],
      date: padDay(planDue),
      due: planDue,
      dueTone: 'info',
    },
    {
      id: 'esc-4',
      kind: 'pattern',
      group: 'later',
      title: 'Panel B night shifts — recovery pattern',
      financial: joinParts(panelBParts),
      financialParts: panelBParts,
      // [syn] Working hypotheses the Operations Superintendent tests; no count is claimed.
      hypotheses: ['Larger share of the 18:00–24:00 peak-reagent window', 'Harder ore domains on Panel B shifts'],
      owner: '',
      decision: 'Superintendent tests the hypotheses with the Met team and the Panel B supervisor before escalating',
      options: [`Note · review ${panelBDue}`],
      seats: [{ role: 'Owns', holder: 'K. Brennan · Ops Supt' }],
      date: padDay(panelBDue),
      due: panelBDue,
      dueTone: 'neutral',
    },
  ];
}

/** Escalation counts shared by the strip cell and the rail header. They count
 * open cards only: a card whose decision is recorded leaves the counts but
 * stays in the rail with its record (`cards` is every card, in rail order).
 * K13b: a case whose living-FMEA change waits on the Senior met seat is an open
 * decision too, so it joins `decisions` while it waits and leaves once approved. */
export function escalationCounts(
  state: SessionState,
  recorded: ReadonlySet<string> = new Set(),
  fmeaLearning?: FmeaLearningSessionState,
) {
  const cards = escalationCards(state);
  const open = cards.filter((card) => !recorded.has(card.id));
  const decisions = open.filter((card) => card.kind === 'decision');
  const patterns = open.filter((card) => card.kind === 'pattern');
  const today = open.filter((card) => card.group === 'today');
  const todayRecorded = cards.filter((card) => card.group === 'today' && recorded.has(card.id));
  const fmeaProposals = fmeaLearning ? fmeaLearning.proposals.size : 0;
  return {
    cards,
    open,
    decisions: decisions.length + fmeaProposals,
    patterns: patterns.length,
    today,
    todayRecorded,
    fmeaProposals,
  };
}

/**
 * K13b: the rail's FMEA cards. One for every case whose living-FMEA change is a
 * wait on the Senior met seat (`proposed`), plus the ones the seat has already
 * approved (`live`) so the approved card keeps its stamp. `fmeaCasePhase()` and
 * `fmeaCaseRead()` do the phase and diff reads; no Advisory state is added here.
 */
export type FmeaRailCard = {
  id: string;
  caseId: string;
  caseCode: string;
  /** The workbook failure mode the case teaches. */
  failureMode: string;
  /** `O 7 → 8 · RPN 224 → 256`, the one line of what changes. */
  changeLine: string;
  occurrence: { value: string; previous?: string };
  rpn: { value: string; previous?: string };
  /** The case's learned `+` lines, read from the proposal. */
  learned: readonly { kind: 'cause' | 'warning'; text: string }[];
  seatLabel: string;
  status: 'proposed' | 'approved';
  /** `Approved · 11:30 · Senior metallurgist` once the seat approves. */
  stamp?: string;
  /** The gate for the acting persona, as the case page computes it. */
  gate: 'approve' | 'await-seat' | 'approved';
};

export function fmeaRailCards(
  fmeaLearning: FmeaLearningSessionState | undefined,
  input: { canApprove: boolean; seatHolderLabel: string },
): FmeaRailCard[] {
  if (!fmeaLearning) return [];
  const caseIds = [...new Set([...fmeaLearning.proposals.keys(), ...fmeaLearning.approvals.keys()])];
  return caseIds.flatMap((caseId) => {
    // D19 phase read: only a case whose change is proposed or live has a card.
    if (fmeaCasePhase(fmeaLearning, caseId) === 'none') return [];
    const proposal = fmeaLearning.proposals.get(caseId);
    const approval = fmeaLearning.approvals.get(caseId);
    const mode = proposal?.failureMode
      ?? (approval ? FAILURE_MODES.find((row) => row.id === approval.failureModeId) : undefined);
    if (!mode) return [];
    const read = fmeaCaseRead({
      mode,
      state: fmeaLearning,
      caseId,
      seatHolderLabel: input.seatHolderLabel,
      canApprove: input.canApprove,
    });
    const { occurrence, rpn } = read.row;
    const before = (cell: { value: string; previous?: string }) => cell.previous ?? cell.value;
    return [{
      id: `fmea-${caseId}`,
      caseId,
      caseCode: proposal?.caseCode ?? approval?.caseCode ?? caseId,
      failureMode: read.row.failureMode,
      changeLine: `O ${before(occurrence)} → ${occurrence.value} · RPN ${before(rpn)} → ${rpn.value}`,
      occurrence: { value: occurrence.value, ...(occurrence.previous ? { previous: occurrence.previous } : {}) },
      rpn: { value: rpn.value, ...(rpn.previous ? { previous: rpn.previous } : {}) },
      learned: [
        ...(read.row.learnedCause ? [{ kind: 'cause' as const, text: read.row.learnedCause }] : []),
        ...(read.row.learnedEarlyWarning ? [{ kind: 'warning' as const, text: read.row.learnedEarlyWarning }] : []),
      ],
      seatLabel: read.seatLabel,
      status: read.phase === 'live' ? 'approved' : 'proposed',
      ...(read.pill?.tone === 'approved' ? { stamp: read.pill.text } : {}),
      gate: read.gate === 'approve' ? 'approve' : read.gate === 'approved' ? 'approved' : 'await-seat',
    }];
  });
}

/** `BM01 stop` — the short name the strip and the rail header give a due-today card. */
export function escalationShortName(card: EscalationCard): string {
  return `${card.title.split(' ')[0]} stop`;
}

export type DecisionRailHeadCell = { id: string; label: string; value: string; caption: string; tone?: 'bad' };

/** The rail header's three cells: due today · capital for review · next week. */
export function decisionRailHead(
  state: SessionState,
  recorded: ReadonlySet<string> = new Set(),
): DecisionRailHeadCell[] {
  const { open, today, todayRecorded } = escalationCounts(state, recorded);
  const capital = open.filter((card) => card.group === 'capital');
  const later = open.filter((card) => card.group === 'later');
  const codes = (list: readonly EscalationCard[]) =>
    list.map((card) => card.title.match(/\((IMP-\d+)\)/)?.[1] ?? card.title).join(' · ');
  const laterCaption = later
    .map((card) => `${card.kind === 'pattern' ? 'Panel B pattern' : 'plan revision'} ${card.due}`)
    .join(' · ');
  return [
    {
      id: 'today',
      label: 'Due today',
      value: `${today.length} decision${today.length === 1 ? '' : 's'}`,
      caption: [
        ...today.map((card) => `${escalationShortName(card)} · ${card.due.replace(/^Today /, '')}`),
        ...todayRecorded.map((card) => `${escalationShortName(card)} recorded`),
      ].join(' · '),
      tone: today.length > 0 ? 'bad' : undefined,
    },
    {
      id: 'capital',
      label: 'Capital for review',
      value: `${capital.length} business case${capital.length === 1 ? '' : 's'}`,
      caption: capital.length > 0 ? `${codes(capital)} · ${capital[0]?.due ?? ''}` : '',
    },
    { id: 'later', label: 'Next week', value: String(later.length), caption: laterCaption },
  ];
}

export type DecisionRecordLine = { recorded: string; awaiting?: string };

/** What the gate shows once the decision is recorded: the stamp, and who still confirms. */
export function decisionRecordLine(
  card: EscalationCard,
  record: { decision: string; recordedAt: string; recordedBy: string },
): DecisionRecordLine {
  const [date, time] = record.recordedAt.split('T');
  const [, month, day] = (date ?? '').split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const stamp = `${day} ${months[Number(month) - 1]} ${time}`;
  const by = card.kind === 'pattern' ? `noted by ${record.recordedBy}` : record.recordedBy;
  const confirmer = card.seats.find((seat) => seat.confirmer)?.confirmer;
  const approved = record.decision === card.options[0];
  return {
    recorded: `Recorded · ${stamp} · ${by}`,
    awaiting: confirmer && approved ? `Awaiting confirm · ${confirmer}` : undefined,
  };
}

export type ForecastScenario = {
  id: string;
  scenario: string;
  tonnes: string;
  vsBudget: string;
  znRecH2: string;
  nsr: string;
  ebitda: string;
  emphasis?: 'row' | 'muted';
};

export function threeScenarioForecast(): ForecastScenario[] {
  const g = gmMoney();
  const f = g.forecast;
  return [
    ...f.scenarios.map((s): ForecastScenario => ({
      id: `sc-${s.name.toLowerCase()}`, scenario: s.name,
      tonnes: Math.round(s.fullYearTonnes).toLocaleString('en-AU'),
      vsBudget: `${s.vsBudgetPct >= 0 ? '+' : '−'}${Math.abs(s.vsBudgetPct).toFixed(1)}%`,
      znRecH2: `${s.restOfYearZnRecoveryPct.toFixed(1)}%`,
      nsr: gmAmount(s.fullYearNsr), ebitda: gmAmount(s.ebitda),
      emphasis: s.name === 'Central' ? 'row' : undefined,
    })),
    {id:'sc-budget',scenario:'Budget · annual weighted Zn',tonnes:Math.round(f.budget.tonnes).toLocaleString('en-AU'),vsBudget:'0.0%',znRecH2:`${f.budget.znRecovery.toFixed(1)}%`,nsr:gmAmount(f.budget.nsr),ebitda:gmAmount(f.budget.ebitda),emphasis:'muted'},
  ];
}
export function forecastCostBasis(): string {
  const g = gmMoney();
  return `Operating EBITDA · NSR − tonnes × ${CURRENCY_GLYPH}${g.actualCostPerSt.toFixed(2)}/st closed-month cost; budget ${CURRENCY_GLYPH}${g.budgetCostPerSt.toFixed(2)}/st`;
}

export type BudgetRiskRow = { id: string; tone: VizTone; label: string; detail: string };

/** The Zn-leverage read under the bridge: cursor Zn recovery against design,
 * and what one Zn point is worth a year — the Advisory value band's own
 * `recoveryPointAnnualA`, so the landing and Advisory cannot disagree. */
export function gmLeverageInsight(state: SessionState): { headline: string; comparison: string; lead: string } {
  const r = recoveryReading('rd-kpi-zn-rec', state);
  const actual = r.value ?? 0;
  const design = r.design ?? 0;
  return {
    headline: 'Zn recovery is the highest-leverage variable',
    comparison: `${actual.toFixed(1)}% vs ${design.toFixed(1)}% design`,
    lead: `+1 Zn point → ${formatAnnualA(valueBandRead(state).recoveryPointAnnualA)}`,
  };
}

export function budgetRiskRows(): BudgetRiskRow[] {
  const bearingStop = gmMoney().bearingStop;
  const amount = (value: number) => `${CURRENCY_GLYPH}${value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const g = gmMoney();
  const low = g.forecast.scenarios.find(s=>s.name==='Conservative')!;
  const central = g.forecast.scenarios.find(s=>s.name==='Central')!;
  return [
    {id:'risk-1',tone:'warn',label:'Zn recovery downside — conservative vs budget',detail:`${low.restOfYearZnRecoveryPct.toFixed(1)}% vs central ${central.restOfYearZnRecoveryPct.toFixed(1)}% for the rest of Year 1 · combined recovery/volume NSR ${gmAmount(low.fullYearNsr)} vs ${gmAmount(g.forecast.budget.nsr)} annual budget`},
    {id:'risk-2',tone:'neutral',label:'BM01 unplanned bearing failure before inspection',detail:`${bearingStop.avoidedFailureHours} h avoided failure ${amount(bearingStop.avoidedFailureAmount)} vs ${bearingStop.plannedHours} h planned inspection ${amount(bearingStop.plannedAmount)}`},
    {id:'risk-3',tone:'warn',label:'Ore hardness — conservative throughput vs budget',detail:`${Math.round(low.fullYearTonnes).toLocaleString()} dst vs ${Math.round(g.forecast.budget.tonnes).toLocaleString()} dst annual budget · ${low.vsBudgetPct >= 0 ? '+' : '−'}${Math.abs(low.vsBudgetPct).toFixed(1)}%`},
    {id:'risk-4',tone:'neutral',label:'Reagent cost — volume-driven, closed MTD',detail:`${gmSignedAmount(g.reagentOverrun)} on +${(g.tonnes-g.budgetTonnes).toLocaleString()} dst · ${CURRENCY_GLYPH}${g.reagentCostPerSt.toFixed(2)}/dst actual = budget rate`},
  ];
}

export type MtdVsBudgetRow = { id: string; metric: string; budget: string; actual: string; variance: string; tone: VizTone };

function recoveryReading(id: string, state: SessionState) {
  const projected = readingsAt(state).find((r) => r.id === id);
  const seed = readingByIdLookup(id);
  if (!seed) throw new Error(`Missing reading ${id}`);
  return projected ?? seed;
}

function recoveryMtdRow(
  id: string,
  state: SessionState,
  metric: string,
): MtdVsBudgetRow {
  const reading = recoveryReading(id, state);
  const actual = reading.value ?? 0;
  const budget = reading.target ?? 0;
  const delta = recoveryPtDelta(actual, budget);
  const tone = gmBudgetTone(actual,budget);
  return {
    id,
    metric,
    budget: `${budget.toFixed(1)}%`,
    actual: `${actual.toFixed(1)}%`,
    variance: `${delta >= 0 ? '+' : ''}${delta.toFixed(1)} pt`,
    tone,
  };
}

export function mtdPerformanceVsBudgetRows(state: SessionState): MtdVsBudgetRow[] {
  const g = gmMoney();
  const m4 = PLANNING_MONTH_PROFILE.find(m=>m.id==='m4')!;
  const money = (id:string,key:'cost'|'revenue'|'ebitda',label:string): MtdVsBudgetRow => {
    const m = g.mtd[key];
    return {id,metric:label,budget:gmThousands(m.target),actual:gmThousands(m.actual),variance:`${m.actual>=m.target?'+':''}${gmThousands(m.actual-m.target)}`,tone:m.tone};
  };
  return [
    {id:'v-1',metric:'Complete tonnes (dst)',budget:g.budgetTonnes.toLocaleString(),actual:g.tonnes.toLocaleString(),variance:`${g.tonnes>=g.budgetTonnes?'+':''}${(g.tonnes-g.budgetTonnes).toLocaleString()}`,tone:gmBudgetTone(g.tonnes,g.budgetTonnes)},
    recoveryMtdRow('rd-kpi-pb-rec',state,'Pb recovery · now (%)'),
    recoveryMtdRow('rd-kpi-zn-rec',state,'Zn recovery · now (%)'),
    {id:'v-4',metric:'Plant availability (%)',budget:`${ANNUAL_PLAN_BUDGETS.millAvailabilityPct.toFixed(1)}%`,actual:`${m4.actualMillAvailabilityPct!.toFixed(1)}%`,variance:`${m4.actualMillAvailabilityPct!-ANNUAL_PLAN_BUDGETS.millAvailabilityPct>=0?'+':''}${(m4.actualMillAvailabilityPct!-ANNUAL_PLAN_BUDGETS.millAvailabilityPct).toFixed(1)} pt`,tone:gmBudgetTone(m4.actualMillAvailabilityPct!,ANNUAL_PLAN_BUDGETS.millAvailabilityPct)},
    money('v-5','cost',`Total costs (${CURRENCY_GLYPH}K)`),
    money('v-6','revenue',`Revenue (${CURRENCY_GLYPH}K)`),
    money('v-7','ebitda',`Operating EBITDA (${CURRENCY_GLYPH}K)`),
  ];
}
